import { createFramesClient } from "@connect/data/frames/client";
import { clearCredentials, getCredentials } from "@connect/credentials-storage";
import type { paths } from "@connect/data/frames/schema";
import { log } from "@connect/observability";
import { ConnectionStatus } from "@moonpay/platform-protocol/models/connection";
import { errorCode, parseErrorBody } from "../../../features/partner-auth";
import { forwardCredentials } from "../../../lib/route-authorize";
import { updateObservabilityCustomerId } from "../../../lib/observability-setup";
import { logCredentialsDiagnostics } from "../../../lib/credentials-diagnostics";
import type { MessageMeta } from "../../../lib/external-messages.types";
import type { createExternalMessages } from "../external-messages";

type AuthorizeResponse =
  paths["/platform/v1/partner/authorize"]["post"]["responses"][200]["content"]["application/json"];

type OnboardingEmitter = Pick<
  ReturnType<typeof createExternalMessages>,
  "active" | "pending" | "unavailable" | "connectionFailedGeneric" | "failed"
>;

export interface FinalizeOnboardingConnectionParams {
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  publicKey: string;
  retryWait?: (delayMs: number) => Promise<void>;
  out: OnboardingEmitter;
}

export type FinalizeOnboardingResult = { kind: "completed"; status: string } | { kind: "error" };

const GENERIC_COMPLETE_ERROR = "Unable to complete connection.";
const AUTHORIZE_RETRY_DELAYS_MS = [500, 1_000, 2_000] as const;
const wait = (delayMs: number) => new Promise<void>((resolve) => setTimeout(resolve, delayMs));

function metaFromResponse(startedAt: number, httpStatus: number): MessageMeta {
  return {
    durationMs: Math.round(performance.now() - startedAt),
    httpStatus,
  };
}

export async function finalizeOnboardingConnection(
  params: FinalizeOnboardingConnectionParams,
): Promise<FinalizeOnboardingResult> {
  const { customerToken, clientToken } = await getCredentials();
  if (!customerToken) {
    await params.out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
      reason: "missing_customer_token",
    });
    return { kind: "error" };
  }
  if (!clientToken) {
    await params.out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
      reason: "missing_client_token",
    });
    return { kind: "error" };
  }

  const client = createFramesClient({
    baseUrl: params.apiBaseUrl,
    headers: {
      ...params.clientHeaders,
      "x-client-token": clientToken,
      Authorization: `Bearer ${customerToken}`,
    },
  });

  const authorizeStartedAt = performance.now();
  try {
    for (let attempt = 0; ; attempt += 1) {
      const {
        data: authorizeData,
        error: authorizeError,
        response: authorizeResponse,
      } = await client.POST("/platform/v1/partner/authorize", {});
      const meta = metaFromResponse(authorizeStartedAt, authorizeResponse.status);
      if (authorizeError || !authorizeData) {
        return await handleAuthorizeFailure(authorizeError, authorizeResponse.status, meta, params);
      }
      const retryDelay = AUTHORIZE_RETRY_DELAYS_MS[attempt];
      if (authorizeData.status === "kyc_required" && retryDelay !== undefined) {
        await (params.retryWait ?? wait)(retryDelay);
        continue;
      }
      return await routeOnboardingAuthorize(authorizeData, params.out, params.publicKey, meta);
    }
  } catch (caught) {
    log.warn("connect.onboarding.finalize_failed", {
      reason: "authorize_failed",
      error: caught instanceof Error ? caught.message : String(caught),
    });
    await params.out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
      reason: "authorize_failed",
    });
    return { kind: "error" };
  }
}

async function handleAuthorizeFailure(
  error: unknown,
  httpStatus: number,
  meta: MessageMeta,
  params: FinalizeOnboardingConnectionParams,
): Promise<FinalizeOnboardingResult> {
  const code = errorCode(parseErrorBody(error));

  if (httpStatus === 422 || code === "unprocessable_entity") {
    await params.out.failed(code ?? "unprocessable_entity", {
      ...meta,
      reason: code ?? "unprocessable_entity",
    });
    return { kind: "completed", status: ConnectionStatus.failed };
  }

  if (httpStatus === 409 || code === "conflict") {
    await logCredentialsDiagnostics("clear_finalize_conflict");
    await clearCredentials();
    await params.out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
      ...meta,
      reason: code ?? "conflict",
    });
    return { kind: "error" };
  }

  await params.out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
    ...meta,
    reason: code ?? "authorize_failed",
  });
  return { kind: "error" };
}

export async function routeOnboardingAuthorize(
  data: AuthorizeResponse,
  out: OnboardingEmitter,
  publicKey: string,
  meta: MessageMeta,
): Promise<FinalizeOnboardingResult> {
  // Sync customerId to observability before any status branch emits, so
  // postMessage logs are attributed on every outcome. See DEVXP-1985.
  updateObservabilityCustomerId(data.customerId);

  switch (data.status) {
    case "unavailable":
      await out.unavailable(meta);

      return { kind: "completed", status: ConnectionStatus.unavailable };
    case "pending":
    case "kyc_required":
      await out.pending(meta);

      return { kind: "completed", status: ConnectionStatus.pending };
    case "active":
    case "terms_acceptance_required":
      break;
    case "connection_required":
      await out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
        ...meta,
        reason: "connection_required",
      });
      return { kind: "error" };
    default:
      await out.connectionFailedGeneric(GENERIC_COMPLETE_ERROR, {
        ...meta,
        reason: "unknown_status",
      });
      return { kind: "error" };
  }

  const { credentials, capabilities } = await forwardCredentials(data, publicKey);

  await out.active({ credentials, capabilities, customerId: data.customerId ?? "", meta });

  return { kind: "completed", status: ConnectionStatus.active };
}
